import React, { useState } from "react";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Box from "@amzn/meridian/box";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Toggle from "@amzn/meridian/toggle";
import Input from "@amzn/meridian/input";
import Divider from "@amzn/meridian/divider";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useWidgetLocalization } from "../util/WidgetLocalizationUtil";
import {
  EDIT_WIDGET_CONFIG,
  TRIGGERED_VISIBILITY_SETTINGS,
  DAILY_TIME_PERIOD,
  START_TIME,
  END_TIME,
  SELECT_START_TIME,
  SELECT_END_TIME,
  ALERT_SETTINGS,
  ALERT_CONDITION,
  THRESHOLD_VALUE,
  THRESHOLD_VALUE_PLACEHOLDER,
  ALERT_TRIGGER_MESSAGE,
  CANCEL,
  SAVE_CHANGES,
} from "../constants/Constants";

const EditWidgetConfigPanel = ({ isOpen, onClose, widgetId, widgetConfig }) => {
  const widget =
    widgetConfig.widgetDefinition || widgetConfig.widget || widgetConfig;

  // Get localized widget content
  const { title: localizedTitle } = useWidgetLocalization(widget);

  // State for widget configuration - initialize from widget.visibility if available
  const [isVisible, setIsVisible] = useState(false);
  const [startTime, setStartTime] = useState(
    (widget.visibility && widget.visibility.startTime) || "",
  );
  const [endTime, setEndTime] = useState(
    (widget.visibility && widget.visibility.endTime) || "",
  );
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [alertThreshold, setAlertThreshold] = useState("");

  const handleClose = () => {
    console.log("EditWidgetConfigPanel: Closing modal");
    onClose();
  };

  const handleSave = () => {
    console.log("EditWidgetConfigPanel: Saving configuration:", {
      widgetId,
      isVisible,
      startTime,
      endTime,
      alertsEnabled,
      alertThreshold,
    });

    // Update widget.visibility with the time values
    if (widget.visibility) {
      widget.visibility.startTime = startTime;
      widget.visibility.endTime = endTime;
    } else {
      widget.visibility = {
        startTime: startTime,
        endTime: endTime,
      };
    }
    // TODO: Implement additional save logic for other properties
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={
        <FormattedMessage
          id="mySCC.editWidgetConfig.title"
          defaultMessage={EDIT_WIDGET_CONFIG}
        />
      }
      width={700}
    >
      {/* Widget Header Section */}
      <Box spacingInset="small">
        <Text type="h400">{localizedTitle}</Text>
      </Box>

      {/* Content Container */}
      <Box spacingInset="medium">
        {/* Display Settings Section */}
        <Box spacingInset="small" type="fill">
          <Row alignmentVertical="center" alignmentHorizontal="justify">
            <Text type="h100">
              <FormattedMessage
                id="mySCC.editWidgetConfig.triggeredVisibilitySettings"
                defaultMessage={TRIGGERED_VISIBILITY_SETTINGS}
              />
            </Text>
            <Toggle
              checked={isVisible}
              onChange={setIsVisible}
              label={isVisible ? "Visible" : "Hidden"}
              disabled={true}
            />
          </Row>
        </Box>

        {/* Time Range Selection */}
        <Box spacingInset="medium large">
          <Box spacingInset="none none small none">
            <Text type="b200" color={isVisible ? "primary" : "secondary"}>
              <FormattedMessage
                id="mySCC.editWidgetConfig.dailyTimePeriod"
                defaultMessage={DAILY_TIME_PERIOD}
              />
            </Text>
          </Box>
          <Row spacing="medium" alignmentVertical="center">
            <Column>
              <Text type="b100" color={isVisible ? "primary" : "secondary"}>
                <FormattedMessage
                  id="mySCC.editWidgetConfig.startTime"
                  defaultMessage={START_TIME}
                />
              </Text>
              <Input
                type="time"
                value={startTime}
                onChange={setStartTime}
                placeholder={SELECT_START_TIME}
                disabled={!isVisible}
              />
            </Column>
            <Column>
              <Text type="b100" color={isVisible ? "primary" : "secondary"}>
                <FormattedMessage
                  id="mySCC.editWidgetConfig.endTime"
                  defaultMessage={END_TIME}
                />
              </Text>
              <Input
                type="time"
                value={endTime}
                onChange={setEndTime}
                placeholder={SELECT_END_TIME}
                disabled={!isVisible}
              />
            </Column>
          </Row>
        </Box>

        <Divider spacingBefore="medium" spacingAfter="medium" />

        {/* Alert Settings Section with toggle in header */}
        <Box spacingInset="small" type="fill">
          <Row alignmentVertical="center" alignmentHorizontal="justify">
            <Text type="h100">
              <FormattedMessage
                id="mySCC.editWidgetConfig.alertSettings"
                defaultMessage={ALERT_SETTINGS}
              />
            </Text>
            <Toggle
              checked={alertsEnabled}
              onChange={setAlertsEnabled}
              label={alertsEnabled ? "Enabled" : "Disabled"}
              disabled={true}
            />
          </Row>
        </Box>

        {/* Alert Threshold Configuration - always visible but disabled when alerts off */}
        <Box spacingInset="medium large">
          <Box spacingInset="none none small none">
            <Text type="b200" color={alertsEnabled ? "primary" : "secondary"}>
              <FormattedMessage
                id="mySCC.editWidgetConfig.alertCondition"
                defaultMessage={ALERT_CONDITION}
              />
            </Text>
          </Box>
          <Row spacing="medium" alignmentVertical="center">
            <Column width="150px">
              <Text type="b100" color={alertsEnabled ? "primary" : "secondary"}>
                <FormattedMessage
                  id="mySCC.editWidgetConfig.thresholdValue"
                  defaultMessage={THRESHOLD_VALUE}
                />
              </Text>
              <Input
                type="number"
                value={alertThreshold}
                onChange={setAlertThreshold}
                placeholder={THRESHOLD_VALUE_PLACEHOLDER}
                disabled={!alertsEnabled}
              />
            </Column>
          </Row>
          <Box spacingInset="small none none none">
            <Text type="b100" color="secondary">
              <FormattedMessage
                id="mySCC.editWidgetConfig.alertTriggerMessage"
                defaultMessage={ALERT_TRIGGER_MESSAGE}
                values={{ threshold: alertThreshold || "[threshold]" }}
              />
            </Text>
          </Box>
        </Box>
      </Box>

      {/* Updated Footer with Cancel, Save layout */}
      <ModalFooter>
        <Row alignmentHorizontal="justify" width="100%">
          <Button type="secondary" onClick={handleClose}>
            <FormattedMessage
              id="mySCC.editWidgetConfig.cancel"
              defaultMessage={CANCEL}
            />
          </Button>
          <Row spacing="medium">
            <Button type="primary" onClick={handleSave}>
              <FormattedMessage
                id="mySCC.editWidgetConfig.save"
                defaultMessage={SAVE_CHANGES}
              />
            </Button>
          </Row>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

EditWidgetConfigPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  widgetId: PropTypes.string,
  widgetConfig: PropTypes.object,
};

EditWidgetConfigPanel.defaultProps = {
  widgetId: null,
  widgetConfig: null,
};

export default EditWidgetConfigPanel;



// WEBPACK FOOTER //
// ./src/components/mySCC/components/EditWidgetConfigPanel.js